// Billing API бэкенда (taskpal_backend/BILLING.md, «Клиентский API»).
// Чистые функции без store и DOM: что показать на экране тарифов по ответу
// GET /api/billing/subscription, ключи ошибок, ожидание активации подписки.
// План меняет только сервер (OUT_ChangePlan), клиент лишь ведёт в Stripe.

export const BILLING_QUERY_KEY = "billing";

export const BILLING_RETURN = {
  SUCCESS: "success",
  CANCEL: "cancel",
  PORTAL: "portal",
};

export const INTERVAL = {
  MONTH: "month",
  YEAR: "year",
};

const STORE_NAMES = {
  apple: "App Store",
  google: "Google Play",
};

// тексты — в локалях settings.upgrade.*; коды ошибок с переводом в settings.upgrade.errors.*
export const I18N_PREFIX = "settings.upgrade";

const ERROR_CODES = [
  "managed_by_store",
  "payments_disabled",
  "price_not_configured",
  "plan_not_found",
  "no_stripe_customer",
  "billing_error",
];

export function escapeHtml(value) {
  return String(value == null ? "" : value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/** Ссылки Checkout/Portal приходят от сервера; уходим только по https. */
export function isSafeRedirectUrl(url) {
  return typeof url == "string" && /^https:\/\//i.test(url);
}

export function storeName(provider) {
  return STORE_NAMES[provider] || "App Store / Google Play";
}

export function isStoreProvider(provider) {
  return Object.prototype.hasOwnProperty.call(STORE_NAMES, provider);
}

/** Ключ перевода для кода ошибки Billing API; неизвестный код — общая ошибка. */
export function billingErrorKey(code) {
  const known = ERROR_CODES.includes(code) ? code : "billing_error";
  return `${I18N_PREFIX}.errors.${known}`;
}

/** Интервалы, для которых у плана есть цена в Stripe. */
export function planIntervals(plan) {
  if (!plan) return [];
  const intervals = [];
  if (plan.monthly_subscription_id) intervals.push(INTERVAL.MONTH);
  if (plan.yearly_subscription_id) intervals.push(INTERVAL.YEAR);
  return intervals;
}

/** Выбранный интервал, если он доступен, иначе первый доступный. */
export function resolveInterval(plan, wanted) {
  const intervals = planIntervals(plan);
  if (intervals.includes(wanted)) return wanted;
  return intervals[0] || null;
}

/** План, к которому относится подписка: её plan_id, иначе текущий план пользователя. */
export function subscribedPlanId(billing, currentPlanId) {
  const sub = billing && billing.subscription;
  return (sub && sub.plan_id) || currentPlanId;
}

/**
 * Что показать на карточке плана:
 * - "none"      — без кнопок (план по умолчанию, нет цен, план назначен вручную);
 * - "subscribe" — первая покупка через Stripe Checkout;
 * - "manage"    — карточка подписки на Stripe: Customer Portal;
 * - "change"    — другой план при подписке на Stripe: смена в Portal;
 * - "store"     — карточка подписки из App Store / Google Play: только текст.
 */
export function planCardAction({ plan, billing, currentPlanId }) {
  if (!plan || plan.default === "on") return "none";

  const managedBy = billing ? billing.managed_by : null;
  const isSubscribed = plan._id === subscribedPlanId(billing, currentPlanId);

  if (isStoreProvider(managedBy)) return isSubscribed ? "store" : "none";
  if (managedBy === "stripe") return isSubscribed ? "manage" : "change";
  if (isSubscribed) return "none";
  return planIntervals(plan).length ? "subscribe" : "none";
}

/** Предупреждения по текущей подписке: отменена (дата окончания) и неуплата. */
export function subscriptionNotices(billing) {
  const sub = billing && billing.subscription;
  if (!sub) return { endsAt: null, paymentFailed: false };
  return {
    endsAt:
      sub.will_renew === false && sub.current_period_end
        ? sub.current_period_end
        : null,
    paymentFailed: sub.status === "grace",
  };
}

/** downgrade_plan пользователя из billing в OUT_ChangePlan. */
export function downgradeFromBilling(billing) {
  if (billing && billing.pending_plan_id) {
    return {
      plan_id: billing.pending_plan_id,
      date: billing.current_period_end || 0,
    };
  }
  return { plan_id: "", date: 0 };
}

export function withoutBillingQuery(query) {
  const rest = { ...(query || {}) };
  delete rest[BILLING_QUERY_KEY];
  return rest;
}

/** Убирает ?billing=… из адреса без перезагрузки и без смены маршрута. */
export function clearBillingQuery(router) {
  const route = router && router.currentRoute;
  if (!route || !route.query || !(BILLING_QUERY_KEY in route.query)) {
    return Promise.resolve();
  }
  return router
    .replace({
      path: route.path,
      query: withoutBillingQuery(route.query),
      hash: route.hash,
    })
    .catch(() => {});
}

/**
 * Ждёт, пока check() станет true. Проверяет при каждом уведомлении subscribe
 * (например, store.watch после OUT_ChangePlan) и после каждого poll() раз в interval.
 * Возвращает true, если дождались, false — по таймауту.
 */
export function waitForCondition({
  check,
  poll,
  subscribe,
  interval = 2000,
  timeout = 30000,
}) {
  return new Promise((resolve) => {
    let done = false;
    let pollTimer = null;
    let unsubscribe = null;

    const finish = (result) => {
      if (done) return;
      done = true;
      clearTimeout(pollTimer);
      clearTimeout(deadline);
      if (unsubscribe) unsubscribe();
      resolve(result);
    };

    const test = () => {
      if (!done && check()) finish(true);
    };

    // следующий опрос — только после ответа предыдущего, без наложения запросов
    const schedule = () => {
      pollTimer = setTimeout(async () => {
        try {
          if (poll) await poll();
        } catch (e) {
          // сеть может мигать — пробуем до таймаута
        }
        test();
        if (!done) schedule();
      }, interval);
    };

    const deadline = setTimeout(() => finish(Boolean(check())), timeout);

    if (subscribe) {
      unsubscribe = subscribe(test);
      if (done && unsubscribe) unsubscribe();
    }
    test();
    if (!done) schedule();
  });
}
